import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { selectHotel, setCulture } from "../features/hotel/hotelSlice";
import ArrowLeft from "./Svg/ArrowLeft";
import XSmall from "./Svg/XSmall";
import { useTranslation } from "react-i18next";
import Modal from "./Helpers/Modal";
import type { IHotelState } from "../features/hotel/hotelInterface";
import { currentHotelName, setHotelConfiguration } from "../helpers/hotels";
import { hotels } from "../config/hotels";
import { motion } from "framer-motion";
import Check from "./Svg/Check";
import SelectField from "./Form/SelectField";
import Button from "./Button";
import { currentCurrencyName, getCurrencyOptions } from "../helpers/currency";
import {
  currentLanguageFlag,
  currentLanguageName,
  getLanguageOptions,
  syncI18nWithCulture,
} from "../helpers/language";
import ChevronDown from "./Svg/ChevronDown";
import { selectBooking } from "../features/booking/bookingSlice";

function Navbar() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking)
  const [showSelectHotelModal, setShowSelectHotelModal] =
    useState<boolean>(false);
  const [showSelectCultureModal, setShowSelectCultureModal] =
    useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState<string>("");
  const { t } = useTranslation();

  /**
   * Hotel Select Modal
   */
  const [selectedHotel, setSelectedHotel] = useState<IHotelState["HotelCode"]>(
    hotel.HotelCode
  );

  /**
   * Change the selected hotel
   * Get the config and add it to the store, clear all bookings
   * Navigrate to the calendar page
   * @returns Submit the hotel form (change hotel
   */
  const submitHotelForm = () => {
    /**
     * If the selected hotel is the same as the current hotel, just close the modal
     */
    if (selectedHotel === hotel.HotelCode) {
      setShowSelectHotelModal(false);
      return;
    }

    /**
     * Set the new hotel configuration in the store
     */
    setHotelConfiguration({ HotelCode: selectedHotel });

    /**
     * Close the select and navigate to the calendar after a short delay to allow state to update
     */
    setTimeout(() => {
      setShowSelectHotelModal(false);
      navigate("/calendar");
    }, 500);
  };

  /**
   * Set form state for culture modal
   */
  const [formState, setFormState] = useState<{
    Language: IHotelState["Culture"]["LanguageCode"];
    Currency: IHotelState["Culture"]["CurrencyCode"];
  }>({
    Language: hotel?.Culture.LanguageCode || "en",
    Currency: hotel?.Culture.CurrencyCode || "EUR",
  });

  /**
   * When the hotel changes, update the form state to reflect the new hotel's default language and currency
   */
  useEffect(() => {
    setFormState({
      Language: hotel?.Culture.LanguageCode || "en",
      Currency: hotel?.Culture.CurrencyCode || "EUR",
    });
  }, [hotel.Culture]);

  /**
   * Set form values when they change
   * @param obj Object with form values to update the form state
   */
  const handleValueCallback = (obj: { [key: string]: string | boolean }) => {
    setFormState((previousState) => ({ ...previousState, ...obj }));
  };

  /**
   *
   * @returns Submit the culture form (language and currency)
   */
  const submitCultureForm = () => {
    /**
     * If the selected language and currency are the same as the current ones, just close the modal
     */
    if (
      formState.Language === hotel.Culture.LanguageCode &&
      formState.Currency === hotel.Culture.CurrencyCode
    ) {
      setShowSelectCultureModal(false);
      return;
    }

    /**
     * Set the new culture in the store
     */
    dispatch(
      setCulture({
        LanguageCode: formState.Language,
        CultureCode: formState.Language.split("-")[1] || "US",
        CurrencyCode: formState.Currency,
      })
    );

    syncI18nWithCulture(formState.Language);

    setShowSelectCultureModal(false);
    setTimeout(() => {
      navigate(0);
    }, 300);
  };

  /**
   * Set title based on route path
   */
  useEffect(() => {
    const path = location.pathname.replace("/", "");
    if(path === "accommodation" && booking.Reservations.length > 0) {
      setTitle(t(`navBar.title.${path}`) + ` (${booking.Reservations.length})`);
    } else {
      setTitle(t(`navBar.title.${path}`));
    }
  }, [location.pathname, booking.Reservations]);

  /**
   * Go back in history
   */
  const goBack = () => {
    const scenes = [
      "calendar",
      "accommodation",
      // "extras",
      "summary",
      "details",
      "payment",
    ];

    const current = location.pathname.replace(/^\/+/, "").split("/")[0];
    const idx = scenes.indexOf(current);

    if (idx > 0) {
      navigate(`/${scenes[idx - 1]}`);
      return;
    }

    // Fallback to browser history
    navigate(-1);
  };

  // Send a message to the parent window to close the booking module
  const close = () => {
    if (window.self !== window.top) {
      window.parent.postMessage({ type: "CLOSE_BOOKING_MODULE" }, "*");
    }
  };

  return (
    <>
      <div className="w-full">
        <div className="flex justify-between items-center w-full p-4 text-fluid-md">
          {["/calendar", "/success"].includes(location.pathname) ? (
            <div></div>
          ) : (
            <div className="cursor-pointer" onClick={() => goBack()}>
              <ArrowLeft className="size-fluid-5"/>
            </div>
          )}
          <div>{title}</div>
          <div>
            <span className="hidden group-[.is-iframe]/body:block min-[1000px]:hidden cursor-pointer">
              <XSmall onClick={() => close()}  className="size-fluid-5"/>
            </span>
          </div>
        </div>

        {hotel.Name !== undefined &&
          !["/success", "/summary", "/details", "/payment"].includes(
            location.pathname
          ) && (
            <div className="bg-black px-4 py-2.5 flex justify-between items-center text-white text-fluid-sm">
              <div className="flex justify-between items-center gap-1">
                <span
                  onClick={() => setShowSelectHotelModal(!showSelectHotelModal)}
                  className="cursor-pointer"
                >
                  {currentHotelName()}
                </span>
                <ChevronDown className="size-fluid-4" />
              </div>
              <div className="flex gap-4 items-center cursor-pointer">
                <div
                  onClick={() =>
                    setShowSelectCultureModal(!showSelectCultureModal)
                  }
                >
                  <span className="underline text-nowrap">
                    {currentCurrencyName()}
                  </span>
                </div>
                <div
                  className="flex gap-2 items-center"
                  onClick={() =>
                    setShowSelectCultureModal(!showSelectCultureModal)
                  }
                >
                  <span className="underline">{currentLanguageName()}</span>
                  <div className=" flex items-center justify-center">
                    <span className="text-fluid-base">{currentLanguageFlag()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>

      <Modal
        onClick={setShowSelectHotelModal}
        show={showSelectHotelModal}
        title={t("modal.selectHotel.title")}
      >
        <ul className="w-full grid grid-cols-1 gap-2">
          {hotels.map((hotel) => (
            <motion.li
              animate={
                selectedHotel === hotel.HotelCode
                  ? {
                      backgroundColor: "#F3EDE5",
                    }
                  : {}
              }
              className="p-4 rounded-xl cursor-pointer flex justify-between items-center"
              onClick={() => setSelectedHotel(hotel.HotelCode)}
              key={hotel.HotelCode}
            >
              <span>{hotel.Name}</span>
              <motion.div
                animate={
                  selectedHotel === hotel.HotelCode
                    ? { opacity: 1 }
                    : { opacity: 0 }
                }
                className="bg-black text-white rounded-xl h-6 w-6 flex items-center justify-center"
              >
                <Check />
              </motion.div>
            </motion.li>
          ))}
        </ul>
        <div className="mt-4">
          <Button
            text={t("modal.selectHotel.button.label")}
            onClick={() => submitHotelForm()}
          />
        </div>
      </Modal>
      <Modal
        onClick={setShowSelectCultureModal}
        show={showSelectCultureModal}
        title={t("modal.selectCulture.title")}
      >
        <div className="flex flex-col space-y-4 text-fluid-base">
          <SelectField
            label={t("form.language.label")}
            name="Language"
            value={formState.Language}
            error={true}
            options={getLanguageOptions()}
            validate={{ required: true }}
            errorMessages={{
              required: t("form.language.validation.required"),
            }}
            valueCallback={handleValueCallback}
          />

          <SelectField
            label={t("form.currency.label")}
            name="Currency"
            value={formState.Currency}
            error={true}
            options={getCurrencyOptions()}
            validate={{ required: true }}
            errorMessages={{
              required: t("form.currency.validation.required"),
            }}
            valueCallback={handleValueCallback}
          />
        </div>

        <div className="mt-4">
          <Button
            text={t("modal.selectCulture.button.label")}
            onClick={() => submitCultureForm()}
          />
        </div>
      </Modal>
    </>
  );
}

export default Navbar;
